/**
 * Application Context - Global app state management
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/APIClient';
import type { Queue } from '@/services/APIClient';
import { APIErrorHandler } from '@/utils/apiErrorHandler';
import { useAuth } from './AuthContext';
import type { 
  Entry, 
  UserProfile,
  Revision,
  Script,
  Audio
} from '@/types';

interface AppContextValue {
  // State
  entries: Entry[];
  currentEntry: Entry | null;
  selectedEntryId: string | null;
  credits: number;
  userProfile: UserProfile | null;
  language: 'ja' | 'en';
  isProcessing: boolean;
  processingMessage: string;
  
  // Revision State
  revisions: Revision[];
  currentRevision: Revision | null;
  currentScript: Script | null;
  currentAudio: Audio | null;
  
  // Actions
  loadEntries: () => Promise<void>;
  selectEntry: (entryId: string) => Promise<void>;
  selectRevision: (revision: Revision) => Promise<void>;
  reloadEntry: () => Promise<void>;
  createEntry: (urls: string[]) => Promise<{ success: boolean; error?: string }>;
  deleteEntry: (entryId: string) => Promise<boolean>;
  refreshCredits: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  refreshCurrentRevision: () => Promise<void>;
  setCredits: (credits: number) => void;
  setLanguage: (lang: 'ja' | 'en') => void;
  setProcessing: (isProcessing: boolean, message?: string) => void;
  updateRevisionScript: (script: Script) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // State
  const [entries, setEntries] = useState<Entry[]>([]);
  const [currentEntry, setCurrentEntry] = useState<Entry | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [language, setLanguage] = useState<'ja' | 'en'>('ja');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  
  // Revision State
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [currentRevision, setCurrentRevision] = useState<Revision | null>(null);
  const [currentScript, setCurrentScript] = useState<Script | null>(null);
  const [currentAudio, setCurrentAudio] = useState<Audio | null>(null);
  const [pendingRevisionSelection, setPendingRevisionSelection] = useState<Revision | null>(null);

  const loadEntries = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await apiClient.getEntries();
      if (response.success && response.data) {
        setEntries(response.data);
      }
    } catch (error) {
      console.error('AppContext: Failed to load entries - exception:', error);
      APIErrorHandler.handleError(error);
    }
  }, [user]);

  const refreshUserProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await apiClient.getUserProfile();
      if (response.success && response.data) {
        const profileData = response.data;
        setUserProfile(profileData);
        setCredits(profileData.credits);
        setLanguage(profileData.language);
        
        // Immediately update i18n language when profile loads
        if (profileData.language && profileData.language !== 'ja') {
          // Force immediate language change with reload
          import('i18next').then(({ default: i18n }) => {
            // Force reload resources for the new language
            i18n.reloadResources([profileData.language]).then(() => {
              // Now change the language
              return i18n.changeLanguage(profileData.language);
            }).then(() => {
              // Force a re-render by updating state again
              setLanguage(profileData.language);
            }).catch(err => {
              console.error('AppContext: Failed to change language:', err);
            });
          });
        }
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  }, [user]);

  // Load user profile when user changes
  useEffect(() => {
    if (user) {
      refreshUserProfile();
      loadEntries();
    } else {
      // Reset state when user logs out
      setEntries([]);
      setCurrentEntry(null);
      setSelectedEntryId(null);
      setCredits(0);
      setUserProfile(null);
    }
  }, [user, refreshUserProfile, loadEntries]);

  // Force language sync after user profile is loaded
  useEffect(() => {
    if (userProfile?.language && user && userProfile.language !== 'ja') {
      // Small delay to ensure i18n is fully initialized
      setTimeout(() => {
        import('i18next').then(({ default: i18n }) => {
          if (i18n.language !== userProfile.language) {
            // First reload resources, then change language
            i18n.reloadResources([userProfile.language]).then(() => {
              return i18n.changeLanguage(userProfile.language);
            }).then(() => {
            });
          }
        });
      }, 500); // Increase delay to ensure everything is loaded
    }
  }, [userProfile?.language, user]);

  const updateEntryQueues = useCallback(async () => {
    if (!user || entries.length === 0) return;
    
    try {
      // Only update queues for entries that are currently processing
      const processingEntries = entries.filter(entry => 
        entry.queues?.some(q => q.status === 'running' || q.status === 'queued')
      );
      
      if (processingEntries.length === 0) return;
      
      
      // Get queue status for processing entries only
      const queueUpdates = await Promise.all(
        processingEntries.map(async (entry) => {
          try {
            const response = await apiClient.getQueues(entry.entryId);
            if (response.success && response.data) {
              return {
                entryId: entry.entryId,
                queues: response.data
              };
            }
            return { entryId: entry.entryId, queues: entry.queues || [] };
          } catch (error) {
            console.error(`Failed to get queues for entry ${entry.entryId}:`, error);
            return { entryId: entry.entryId, queues: entry.queues };
          }
        })
      );
      
      // Update entries with new queue information
      setEntries(prevEntries => {
        const updatedEntries = prevEntries.map(entry => {
          const queueUpdate = queueUpdates.find(update => update.entryId === entry.entryId);
          return queueUpdate ? { ...entry, queues: queueUpdate.queues } : entry;
        });
        
        // Update currentEntry if it's one of the updated entries
        if (currentEntry) {
          const updatedCurrentEntry = updatedEntries.find(e => e.entryId === currentEntry.entryId);
          if (updatedCurrentEntry && JSON.stringify(updatedCurrentEntry.queues) !== JSON.stringify(currentEntry.queues)) {
            setCurrentEntry(updatedCurrentEntry);
          }
        }
        
        // Check if any processing entries completed
        const stillProcessing = updatedEntries.some(entry => 
          entry.queues?.some((q: Queue) => q.status === 'running' || q.status === 'queued')
        );
        
        const wasProcessing = processingEntries.length > 0;
        
        // Check if script generation completed for current entry
        if (currentEntry) {
          const currentEntryQueueUpdate = queueUpdates.find(update => update.entryId === currentEntry.entryId);
          if (currentEntryQueueUpdate) {
            // Find the most recent script generation queue
            const oldScriptQueues = currentEntry.queues?.filter((q: Queue) => q.queueType === 'generate_script') || [];
            const newScriptQueues = currentEntryQueueUpdate.queues?.filter((q: Queue) => q.queueType === 'generate_script') || [];
            
            // Sort by created date (newest first)
            const oldScriptQueue = oldScriptQueues.sort((a: Queue, b: Queue) => 
              new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
            )[0];
            const newScriptQueue = newScriptQueues.sort((a: Queue, b: Queue) => 
              new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
            )[0];
            
            
            // If script generation just completed
            if (oldScriptQueue?.status === 'running' && newScriptQueue?.status === 'completed') {
              
              // Reload revisions to get the newly created one
              setTimeout(async () => {
                if (selectedEntryId) {
                  const response = await apiClient.getRevisions(selectedEntryId);
                  if (response.success && response.data) {
                    setRevisions(response.data);
                  } else {
                    console.error('Failed to load revisions after script generation:', response.error);
                    setRevisions([]);
                  }
                  
                  // Find and select the newest revision (highest revision number)
                  if (response.data && response.data.length > 0) {
                    // Sort by revision_number descending to get the newest
                    // Revision numbers are in format YYMMDD_HHMMSS, so string comparison works
                    const sortedRevisions = [...response.data].sort((a, b) => 
                      b.revision_number.localeCompare(a.revision_number)
                    );
                    const newestRevision = sortedRevisions[0];
                    // Set a flag to select the new revision after this function completes
                    setPendingRevisionSelection(newestRevision);
                  }
                }
              }, 1000);
            }
            
            // Check for audio generation completion
            const oldAudioQueues = currentEntry.queues?.filter((q: Queue) => q.queueType === 'generate_audio') || [];
            const newAudioQueues = currentEntryQueueUpdate.queues?.filter((q: Queue) => q.queueType === 'generate_audio') || [];
            
            // Sort by created date (newest first)
            const oldAudioQueue = oldAudioQueues.sort((a: Queue, b: Queue) => 
              new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
            )[0];
            const newAudioQueue = newAudioQueues.sort((a: Queue, b: Queue) => 
              new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
            )[0];
            
            // If audio generation just completed
            if (oldAudioQueue?.status === 'running' && newAudioQueue?.status === 'completed') {
              
              // Reload revisions to get updated audio data
              setTimeout(async () => {
                if (selectedEntryId && currentRevision) {
                  const response = await apiClient.getRevisions(selectedEntryId);
                  if (response.success && response.data) {
                    setRevisions(response.data);
                  } else {
                    console.error('Failed to load revisions after audio generation:', response.error);
                    setRevisions([]);
                  }
                  
                  // Find and update the current revision with new audio data
                  const updatedRevision = response.data ? response.data.find(r => r.revision_number === currentRevision.revision_number) : null;
                  if (updatedRevision) {
                    // Re-select the current revision to update audio data
                    setPendingRevisionSelection(updatedRevision);
                  }
                }
              }, 1000);
            }
          }
        }
        
        // If processing just completed, reload full entry data
        if (wasProcessing && !stillProcessing) {
          setTimeout(() => loadEntries(), 1000);
        }
        
        return updatedEntries;
      });
    } catch (error) {
      console.error('AppContext: Failed to update entry queues:', error);
    }
  }, [user, entries, loadEntries, currentEntry, selectedEntryId]);

  // Global queue polling - lightweight updates for processing entries
  useEffect(() => {
    if (!user) return;

    const pollInterval = setInterval(async () => {
      try {
        // Check if any entries are processing
        const hasProcessingEntries = entries.some(entry => 
          entry.queues?.some((q: Queue) => q.status === 'running' || q.status === 'queued')
        );
        
        if (hasProcessingEntries) {
          await updateEntryQueues(); // Only update queue information
        }
      } catch (error) {
        console.error('Failed to poll queue status:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, [user, entries, updateEntryQueues]);

  const selectEntry = useCallback(async (entryId: string) => {
    if (!user) return;
    
    try {
      setSelectedEntryId(entryId);
      
      // 1. Find entry from existing entries array
      const entry = entries.find(e => e.entryId === entryId);
      
      if (entry) {
        setCurrentEntry(entry);
        
        // 2. Load revisions
        const revisionsResponse = await apiClient.getRevisions(entryId);
        if (revisionsResponse.success && revisionsResponse.data) {
          setRevisions(revisionsResponse.data);
        } else {
          console.error('Failed to load revisions:', revisionsResponse.error);
          setRevisions([]);
        }
        
        // 3. Find and load revision - preserve current selection if it exists
        let revisionToLoad;
        const revisionsData = revisionsResponse.success && revisionsResponse.data ? revisionsResponse.data : [];
        
        // If we already have a current revision for this entry, try to keep it
        if (currentRevision && currentRevision.revision_number && entryId === currentEntry?.entryId) {
          revisionToLoad = revisionsData.find(r => r.revision_number === currentRevision.revision_number);
        }
        
        // Otherwise, load the live revision
        if (!revisionToLoad) {
          revisionToLoad = revisionsData.find(r => r.is_live) || revisionsData[0];
        }
        
        if (revisionToLoad) {
          setCurrentRevision(revisionToLoad);
          
          // 4. Extract script and audio from revision data
          if (revisionToLoad.script) {
            // Script ID can be a UUID string or number
            const scriptId = revisionToLoad.script.id || revisionToLoad.script_id || '';
            
            const script: Script = {
              id: scriptId,
              entry_id: entryId,
              user_id: user?.uid || entry.userId || '',
              script_content: revisionToLoad.script.script_content,
              script_file_path: revisionToLoad.script.script_file_path || '',
              script_settings: revisionToLoad.script.script_settings,
              created_at: revisionToLoad.script.created_at,
              updated_at: revisionToLoad.script.created_at,
              revision_number: revisionToLoad.revision_number,
              revision_name: revisionToLoad.revision_name || undefined
            };
            setCurrentScript(script);
          }
          
          if (revisionToLoad.audio) {
            // Generate audio URL based on audio ID - always use revisions folder
            const audioId = revisionToLoad.audio.id;
            const userId = entry.userId || user?.uid || '';
            const audioPath = `https://eccast-files.s3.ap-northeast-1.amazonaws.com/${userId}/audio/revisions/${audioId}.wav`;
            
            const audio: Audio = {
              id: revisionToLoad.audio.id,
              entry_id: entryId,
              user_id: userId,
              script_id: revisionToLoad.script?.id || '',
              audio_file_path: audioPath,
              generation_settings: revisionToLoad.audio.generation_settings,
              created_at: revisionToLoad.audio.created_at,
              duration: parseFloat(revisionToLoad.audio.total_duration) || 0,
              waveform_data: JSON.parse(revisionToLoad.audio.waveform_data || '{}').samples || [],
              revision_number: revisionToLoad.revision_number,
              revision_name: revisionToLoad.revision_name || undefined,
              is_live_audio: revisionToLoad.is_live
            };
            setCurrentAudio(audio);
          }
        } else {
          // No revisions yet, clear current data
          setCurrentRevision(null);
          setCurrentScript(null);
          setCurrentAudio(null);
        }
      } else {
        console.error('AppContext: Entry not found in entries array:', entryId);
      }
    } catch (error) {
      console.error('AppContext: Failed to select entry - exception:', error);
      APIErrorHandler.handleError(error);
    }
  }, [user, entries, currentEntry, currentRevision]);

  const createEntry = useCallback(async (urls: string[]) => {
    if (!user) return { success: false, error: 'Not authenticated' };
    
    try {
      
      const results: Array<{ url: string; success: boolean; result?: any; error?: string }> = [];
      let firstEntryId: string | null = null;
      
      // Process URLs sequentially like the existing system
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        
        try {
          // Create single entry for each URL
          const response = await apiClient.createEntry(url);
          
          if (response.success && response.data) {
            // Handle single entry response
            const entryId = response.data.entryId;
            
            if (entryId) {
              
              // Start product analysis
              const analysisResult = await apiClient.startProductAnalysis(entryId);
              
              if (analysisResult.success) {
                results.push({ url, success: true, result: analysisResult });
                // Store the first successfully created entry ID
                if (!firstEntryId) {
                  firstEntryId = entryId;
                }
              } else {
                throw new Error(`Product analysis failed: ${analysisResult.error}`);
              }
            } else {
              throw new Error('Entry creation failed - no entryId');
            }
          } else {
            throw new Error(response.error || 'Entry creation failed');
          }
          
          
        } catch (error: any) {
          console.error(`❌ Product analysis failed for URL ${i + 1} (${url}):`, error);
          results.push({ url, success: false, error: error.message });
        }
        
        // Add delay between requests (like existing system)
        if (i < urls.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Reload entries after all processing
      await loadEntries();
      
      // Select the first successfully created entry
      if (firstEntryId) {
        setTimeout(() => selectEntry(firstEntryId!), 500);
      }
      
      // Show results summary
      const successCount = results.filter(r => r.success).length;
      
      if (successCount > 0) {
        return { success: true, message: `${successCount} entries created successfully` };
      } else {
        return { success: false, error: 'All entry creations failed' };
      }
      
    } catch (error: any) {
      const apiError = APIErrorHandler.handleError(error);
      return { success: false, error: apiError.message };
    }
  }, [user, loadEntries, selectEntry]);

  const deleteEntry = useCallback(async (entryId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const response = await apiClient.deleteEntry(entryId);
      
      if (response.success) {
        // Remove from local state
        setEntries(prev => prev.filter(e => e.entryId !== entryId));
        
        // Clear current entry if it was deleted
        if (selectedEntryId === entryId) {
          setCurrentEntry(null);
          setSelectedEntryId(null);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to delete entry:', error);
      APIErrorHandler.handleError(error);
      return false;
    }
  }, [user, selectedEntryId]);

  const refreshCredits = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await apiClient.getCredits();
      if (response.success && response.data) {
        setCredits(response.data.balance);
      }
    } catch (error) {
      console.error('Failed to refresh credits:', error);
    }
  }, [user]);

  const setProcessing = useCallback((isProcessing: boolean, message?: string) => {
    setIsProcessing(isProcessing);
    setProcessingMessage(message || '');
  }, []);

  const selectRevision = useCallback(async (revision: Revision) => {
    if (!user || !currentEntry) return;
    
    try {
      // Find the latest revision data from state
      const latestRevision = revisions.find(r => r.revision_number === revision.revision_number) || revision;
      
      setCurrentRevision(latestRevision);
      
      // Extract script and audio from revision data (already loaded)
      if (latestRevision.script) {
        // Script ID can be a UUID string or number
        const scriptId = latestRevision.script.id || latestRevision.script_id || '';
        
        const script: Script = {
          id: scriptId,
          entry_id: currentEntry.entryId,
          user_id: user?.uid || currentEntry.userId || '',
          script_content: latestRevision.script.script_content,
          script_file_path: latestRevision.script.script_file_path || '',
          script_settings: latestRevision.script.script_settings,
          created_at: latestRevision.script.created_at,
          updated_at: latestRevision.script.created_at,
          revision_number: latestRevision.revision_number,
          revision_name: latestRevision.revision_name || undefined
        };
        setCurrentScript(script);
      } else {
        setCurrentScript(null);
      }
      
      if (latestRevision.audio) {
        // Generate audio URL based on audio ID - always use revisions folder
        const audioId = latestRevision.audio.id;
        const userId = currentEntry.userId || user?.uid || '';
        const audioPath = `https://eccast-files.s3.ap-northeast-1.amazonaws.com/${userId}/audio/revisions/${audioId}.wav`;
        
        const audio: Audio = {
          id: latestRevision.audio.id,
          entry_id: currentEntry.entryId,
          user_id: userId,
          script_id: latestRevision.script?.id || '',
          audio_file_path: audioPath,
          generation_settings: latestRevision.audio.generation_settings,
          created_at: latestRevision.audio.created_at,
          duration: parseFloat(latestRevision.audio.total_duration) || 0,
          waveform_data: JSON.parse(latestRevision.audio.waveform_data || '{}').samples || [],
          revision_number: latestRevision.revision_number,
          revision_name: latestRevision.revision_name || undefined,
          is_live_audio: latestRevision.is_live
        };
        setCurrentAudio(audio);
      } else {
        setCurrentAudio(null);
      }
    } catch (error) {
      console.error('Failed to select revision:', error);
      APIErrorHandler.handleError(error);
    }
  }, [user, currentEntry, revisions]);

  // Handle pending revision selection
  useEffect(() => {
    if (pendingRevisionSelection) {
      selectRevision(pendingRevisionSelection);
      setPendingRevisionSelection(null);
    }
  }, [pendingRevisionSelection, selectRevision]);

  const reloadEntry = useCallback(async () => {
    if (selectedEntryId) {
      // Reload both the entry list and the selected entry
      await Promise.all([
        loadEntries(), // Update the history list
        selectEntry(selectedEntryId) // Update the current entry
      ]);
    }
  }, [selectedEntryId, selectEntry, loadEntries]);

  const updateRevisionScript = useCallback((updatedScript: Script) => {
    // Update the script in the revisions array
    setRevisions(prevRevisions => {
      const updatedRevisions = prevRevisions.map(revision => {
        if (revision.revision_number === updatedScript.revision_number && revision.script) {
          return {
            ...revision,
            script: {
              ...revision.script,
              script_content: updatedScript.script_content,
              script_settings: updatedScript.script_settings
            }
          };
        }
        return revision;
      });

      // If this is the current revision, update the current revision state too
      if (currentRevision && currentRevision.revision_number === updatedScript.revision_number) {
        const updatedRevision = updatedRevisions.find(r => r.revision_number === updatedScript.revision_number);
        if (updatedRevision) {
          setCurrentRevision(updatedRevision);
        }
      }

      return updatedRevisions;
    });

    // Update current script if it's the same revision
    if (currentScript && currentScript.revision_number === updatedScript.revision_number) {
      setCurrentScript(updatedScript);
    }
  }, [currentScript, currentRevision]);

  // Refresh current revision data (for updating audio settings after regeneration)
  const refreshCurrentRevision = useCallback(async () => {
    if (!selectedEntryId || !currentRevision || !user) return;
    
    try {
      const response = await apiClient.getRevisions(selectedEntryId);
      if (response.success && response.data) {
        setRevisions(response.data);
        
        // Find the current revision in the updated data
        const updatedRevision = response.data.find(
          r => r.revision_number === currentRevision.revision_number
        );
        
        if (updatedRevision) {
          setCurrentRevision(updatedRevision);
          
          // Update currentAudio with latest generation_settings
          if (updatedRevision.audio) {
            const audioId = updatedRevision.audio.id;
            const userId = currentEntry?.userId || user?.uid || '';
            const audioPath = `https://eccast-files.s3.ap-northeast-1.amazonaws.com/${userId}/audio/revisions/${audioId}.wav`;
            
            const audio: Audio = {
              id: updatedRevision.audio.id,
              entry_id: currentEntry?.entryId || selectedEntryId,
              user_id: userId,
              script_id: updatedRevision.script?.id || '',
              audio_file_path: audioPath,
              generation_settings: updatedRevision.audio.generation_settings,
              created_at: updatedRevision.audio.created_at,
              duration: parseFloat(updatedRevision.audio.total_duration) || 0,
              waveform_data: JSON.parse(updatedRevision.audio.waveform_data || '{}').samples || [],
              revision_number: updatedRevision.revision_number,
              revision_name: updatedRevision.revision_name || undefined,
              is_live_audio: updatedRevision.is_live
            };
            setCurrentAudio(audio);
          } else {
            setCurrentAudio(null);
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh current revision:', error);
    }
  }, [selectedEntryId, currentRevision, user, currentEntry]);

  const value: AppContextValue = {
    // State
    entries,
    currentEntry,
    selectedEntryId,
    credits,
    userProfile,
    language,
    isProcessing,
    processingMessage,
    
    // Revision State
    revisions,
    currentRevision,
    currentScript,
    currentAudio,
    
    // Actions
    loadEntries,
    selectEntry,
    selectRevision,
    reloadEntry,
    createEntry,
    deleteEntry,
    refreshCredits,
    refreshUserProfile,
    refreshCurrentRevision,
    setCredits,
    setLanguage,
    setProcessing,
    updateRevisionScript
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};